import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Show, UserButton } from "@clerk/react";
import { Search, ShoppingBag, User, ShieldCheck, Tag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useMe } from "@/hooks/useMe";
import { CATEGORIES } from "@/lib/api";

const LINKS = [...CATEGORIES.map((c) => ({ id: c.id, label: c.label, to: `/tienda?category=${c.id}` })), { id: "all", label: "Todo", to: "/tienda" }];

const useActive = () => {
  const { pathname, search } = useLocation();
  const cat = new URLSearchParams(search).get("category") || "";
  return (id) => pathname === "/tienda" && (id === "all" ? !cat : cat === id);
};

const SearchBox = ({ className, testid }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    navigate(`/tienda${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };
  return (
    <form onSubmit={submit} className={`items-center bg-slate-100 rounded-full px-4 focus-within:ring-2 focus-within:ring-brand/40 ${className}`}>
      <Search className="w-4 h-4 text-slate-500 shrink-0" />
      <input type="search" name="buscar" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar productos" className="bg-transparent outline-none text-sm ml-2 w-full min-w-0" data-testid={testid} />
    </form>
  );
};

const OutletPill = ({ className, testid }) => (
  <Link to="/outlet" className={`items-center gap-1.5 rounded-full bg-verde text-white font-bold uppercase hover:bg-verde-light transition-colors shrink-0 ${className}`} data-testid={testid}>
    <Tag className="w-3.5 h-3.5" /> Outlet
  </Link>
);

const MobileNav = () => {
  const isActive = useActive();
  return (
    <div className="lg:hidden border-t border-slate-200/80 px-4 py-2 space-y-2" data-testid="mobile-nav">
      <SearchBox className="flex h-9" testid="mobile-search-input" />
      <nav className="flex gap-2 overflow-x-auto text-xs font-semibold uppercase tracking-wide pb-0.5">
        {[LINKS[3], ...LINKS.slice(0, 3)].map((l) => (
          <Link key={l.id} to={l.to} data-testid={`mobile-nav-${l.id}`}
            className={`shrink-0 px-3 h-8 grid place-items-center rounded-full border transition-colors ${isActive(l.id) ? "bg-brand text-white border-brand" : "border-slate-200"}`}>
            {l.label}
          </Link>
        ))}
      </nav>
    </div>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const { me } = useMe();
  const isActive = useActive();

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-md" data-testid="site-header">
      <div className="bg-verde text-white text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.12em] sm:tracking-[0.25em] text-center py-2 px-4" data-testid="promo-bar">
        Envío gratis a partir de 50 € · Cambios y devoluciones 15 días
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 h-16 flex items-center gap-4 xl:gap-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
          <img src="/logo-mark.png" alt="" className="h-10 w-auto" />
          <span className="font-brand leading-none block lg:hidden xl:block">
            <span className="block text-brand text-[13px] sm:text-[15px] tracking-[0.12em]">SOBRINO</span>
            <span className="block text-verde text-[10px] sm:text-[11px] tracking-[0.5em] sm:tracking-[0.55em] mt-0.5">SPORT</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6 text-sm font-semibold uppercase tracking-wide">
          {LINKS.map((l) => (
            <Link key={l.id} to={l.to} className={`nav-link ${isActive(l.id) ? "text-brand nav-link-active" : ""}`} data-testid={`nav-${l.id}`}>{l.label}</Link>
          ))}
        </nav>
        <OutletPill className="hidden lg:inline-flex h-9 px-4 text-xs tracking-[0.15em]" testid="nav-outlet" />
        <SearchBox className="hidden lg:flex ml-auto h-10 lg:w-52 xl:w-72" testid="header-search-input" />
        <div className="flex items-center gap-1 sm:gap-2 ml-auto lg:ml-0">
          <OutletPill className="inline-flex lg:hidden h-8 px-3 text-[11px] tracking-wider" testid="mobile-outlet-button" />
          {me?.role === "admin" && (
            <Link to="/admin" className="icon-btn" title="Panel admin" data-testid="header-admin-link"><ShieldCheck className="w-5 h-5" /></Link>
          )}
          <Show when="signed-out">
            <Link to="/sign-in" className="icon-btn" title="Entrar" data-testid="header-signin-link"><User className="w-5 h-5" /></Link>
          </Show>
          <Show when="signed-in">
            <Link to="/cuenta" className="hidden xl:inline whitespace-nowrap text-xs font-semibold uppercase tracking-wide px-2 hover:text-brand" data-testid="header-account-link">Mi cuenta</Link>
            <UserButton />
          </Show>
          <button onClick={() => setOpen(true)} className="icon-btn relative" data-testid="header-cart-button" aria-label="Carrito">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-brand text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center" data-testid="cart-count-badge">{count}</span>
            )}
          </button>
        </div>
      </div>
      <MobileNav />
    </header>
  );
};
